import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Play, Loader2, Sparkles, Check, User, Lightbulb } from "lucide-react";
import { IconCredit, IconCredit1 } from "@/assets/icons/icons";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import type { VideoStyle, VideoGenerationRequest, UGCAvatarConfig } from "../types/videoStudio.types";
import {
  TONE_OPTIONS,
  AVATAR_GENDER_OPTIONS,
  AVATAR_AGE_OPTIONS,
  AVATAR_SKIN_OPTIONS,
  AVATAR_HAIR_OPTIONS,
  AVATAR_HAIR_COLOR_OPTIONS,
  AVATAR_VIBE_OPTIONS,
  AVATAR_SETTING_OPTIONS,
  PODCAST_SETTING_OPTIONS,
} from "../types/videoStudio.types";

interface VideoStudioSidebarProps {
  selectedStyle: VideoStyle | null;
  onGenerate: (payload: VideoGenerationRequest) => void;
  isGenerating: boolean;
}

const COMBO_FIXED_DURATION = "30" as const;
const COMBO_FIXED_CREDITS = 194;

export function VideoStudioSidebar({
  selectedStyle,
  onGenerate,
  isGenerating,
}: VideoStudioSidebarProps) {
  const [videoTypeId, setVideoTypeId] = useState("");
  const { language } = useAuth();

  // Advanced options
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [userPrompt, setUserPrompt] = useState("");
  // Phase 5.4: removed problemHint state — el field se eliminó del UI porque
  // el director ya tiene product_description + sale_angle del Narrative.
  const [tone, setTone] = useState<string>("");
  const [angle, setAngle] = useState("");

  // Avatar config (UGC only)
  const [avatarGender, setAvatarGender] = useState<UGCAvatarConfig["gender"] | "">("");
  const [avatarAge, setAvatarAge] = useState<UGCAvatarConfig["age_range"] | "">("");
  const [avatarSkin, setAvatarSkin] = useState<UGCAvatarConfig["skin_tone"] | "">("");
  const [avatarHair, setAvatarHair] = useState<UGCAvatarConfig["hair"] | "">("");
  const [avatarHairColor, setAvatarHairColor] = useState<UGCAvatarConfig["hair_color"] | "">("");
  const [avatarVibe, setAvatarVibe] = useState<UGCAvatarConfig["vibe"] | "">("");
  const [avatarSetting, setAvatarSetting] = useState<UGCAvatarConfig["setting"] | "">("");

  const isUGC = selectedStyle?.id === "ugc-testimonial";
  const isModeling = selectedStyle?.id === "product-modeling";
  const isPodcast = selectedStyle?.id === "podcast-style";
  const isSassy = selectedStyle?.id === "sassy-object";
  const isAnimated = selectedStyle?.id === "animated-problem";
  const isCombo = isSassy || isAnimated;
  const needsAvatar = isUGC || isModeling || isPodcast;

  // When switching styles, auto-set video_type_id
  useEffect(() => {
    if (isSassy) {
      setVideoTypeId("sassy-object");
    } else if (isAnimated) {
      setVideoTypeId("animated-problem");
    } else if (isUGC) {
      setVideoTypeId("ugc-testimonial");
    } else if (isModeling) {
      setVideoTypeId("product-modeling");
    } else if (isPodcast) {
      setVideoTypeId("podcast-style");
      setAvatarSetting("studio-dark" as UGCAvatarConfig["setting"]);
    }
  }, [isSassy, isAnimated, isUGC, isModeling, isPodcast]);

  const avatarComplete = needsAvatar
    ? !!avatarGender && !!avatarAge && !!avatarSkin && !!avatarHair && !!avatarHairColor && !!avatarVibe && !!avatarSetting
    : true;

  const effectiveDuration = COMBO_FIXED_DURATION;

  // Phase 5.1: instruction is now optional. The director Gemini can generate
  // a great script with just product name + audience + sale_angle. Forcing the
  // user to write something added friction without improving quality. If they
  // write something, great; if not, the director still works.
  const canGenerate =
    !!selectedStyle &&
    (isCombo || !!tone) &&
    avatarComplete &&
    !isGenerating;

  // Hard char limit on the user instruction so the LLM doesn't get derailed
  // by an essay and the resulting script stays within the 50-word total
  // (25 + 25) that the validators enforce.
  const INSTRUCTION_CHAR_LIMIT = 280;

  const totalCredits = COMBO_FIXED_CREDITS;

  const t = (key: string) => dictionary[key]?.[language as Language] ?? key;

  const handleGenerate = () => {
    if (!selectedStyle) return;
    onGenerate({
      style_id: selectedStyle.id,
      video_type_id: videoTypeId,
      product_name: "",
      product_image: "",
      product_description: "",
      language: "es",
      duration: effectiveDuration as VideoGenerationRequest["duration"],
      user_prompt: userPrompt || undefined,
      // Phase 5.4: problem_hint eliminado del UI, ya no se llena. Lo dejamos
      // siempre en undefined para no romper el tipo de la legacy request.
      problem_hint: undefined,
      tone: tone as VideoGenerationRequest["tone"],
      angle: angle || undefined,
      category_override: null,
      avatar_config: needsAvatar
        ? {
            gender: avatarGender as UGCAvatarConfig["gender"],
            age_range: avatarAge as UGCAvatarConfig["age_range"],
            skin_tone: avatarSkin as UGCAvatarConfig["skin_tone"],
            hair: avatarHair as UGCAvatarConfig["hair"],
            hair_color: avatarHairColor as UGCAvatarConfig["hair_color"],
            vibe: avatarVibe as UGCAvatarConfig["vibe"],
            setting: avatarSetting as UGCAvatarConfig["setting"],
          }
        : null,
    });
  };

  return (
    <div className="w-[40%] border-l border-border flex flex-col flex-shrink-0">
      <div className="p-5 flex-1 overflow-y-auto">
        {/* Title */}
        <div className="mb-4">
          <h3 className="text-sm font-bold text-foreground">{t("video-sidebar-title")}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("video-sidebar-subtitle")}
          </p>
        </div>

        {!selectedStyle ? (
          <div className="rounded-xl border-2 border-dashed border-border/40 p-6 text-center">
            <p className="text-sm text-muted-foreground/60">
              {t("video-select-style")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected style pill */}
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white">
              <div className="w-6 h-6 rounded-md bg-indigo-100/70 flex items-center justify-center flex-shrink-0">
                <Play className="h-3 w-3 text-indigo-500" />
              </div>
              <p className="text-xs font-medium text-foreground truncate">{t(selectedStyle.nameKey)}</p>
              {selectedStyle.estimated_time && (
                <p className="text-[9px] text-muted-foreground/70 ml-auto whitespace-nowrap">
                  {t("video-estimated-time")} {selectedStyle.estimated_time}
                </p>
              )}
            </div>

            {/* Style info banner */}
            {selectedStyle && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-indigo-50/60 border border-indigo-200">
                <Lightbulb className="h-3.5 w-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-indigo-600 leading-relaxed">
                  {t(`video-info-${selectedStyle.id}`)}
                </p>
              </div>
            )}

            {/* Fixed duration indicator — 30s */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50/50">
              <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">
                {t("video-duration")}
              </p>
              <span className="text-xs font-bold text-indigo-700">30s</span>
              <div className="ml-auto flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-indigo-500 text-white">
                <IconCredit1 width="10px" height="10px" />
                {COMBO_FIXED_CREDITS}
              </div>
            </div>

            {/* Avatar Config — UGC and Modeling */}
            {needsAvatar && (
              <div className="rounded-xl border-2 border-indigo-200 bg-white shadow-sm shadow-indigo-100/25 overflow-hidden">
                <div className="flex items-center gap-1.5 px-3 py-2.5 bg-indigo-50/60 border-b border-indigo-100">
                  <User className="h-3.5 w-3.5 text-indigo-500" />
                  <span className="text-xs font-semibold text-indigo-700">{t("video-avatar-title")}</span>
                </div>

                <div className="px-3 pb-3.5 pt-3 space-y-3">
                  {/* Gender */}
                  <AvatarButtonGroup
                    label={t("video-avatar-gender")}
                    options={AVATAR_GENDER_OPTIONS}
                    value={avatarGender}
                    onChange={(v) => setAvatarGender(v as UGCAvatarConfig["gender"])}
                    t={t}
                  />

                  {/* Age */}
                  <AvatarButtonGroup
                    label={t("video-avatar-age")}
                    options={AVATAR_AGE_OPTIONS}
                    value={avatarAge}
                    onChange={(v) => setAvatarAge(v as UGCAvatarConfig["age_range"])}
                    t={t}
                  />

                  {/* Skin tone */}
                  <div>
                    <p className="text-[10px] font-semibold text-indigo-600/80 mb-1.5 uppercase tracking-wider">
                      {t("video-avatar-skin")}
                    </p>
                    <div className="flex gap-2">
                      {AVATAR_SKIN_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setAvatarSkin(opt.value as UGCAvatarConfig["skin_tone"])}
                          className={cn(
                            "flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg border-2 transition-all flex-1",
                            avatarSkin === opt.value
                              ? "border-indigo-500 bg-indigo-50"
                              : "border-border/60 hover:border-foreground/20"
                          )}
                        >
                          <div
                            className="w-6 h-6 rounded-full border border-black/10"
                            style={{ backgroundColor: opt.color }}
                          />
                          <span className="text-[8px] font-medium text-muted-foreground">{t(opt.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Hair */}
                  <AvatarButtonGroup
                    label={t("video-avatar-hair")}
                    options={AVATAR_HAIR_OPTIONS}
                    value={avatarHair}
                    onChange={(v) => setAvatarHair(v as UGCAvatarConfig["hair"])}
                    t={t}
                    columns={3}
                  />

                  {/* Hair color */}
                  <div>
                    <p className="text-[10px] font-semibold text-indigo-600/80 mb-1.5 uppercase tracking-wider">
                      {t("video-avatar-hair-color")}
                    </p>
                    <div className="flex gap-2">
                      {AVATAR_HAIR_COLOR_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setAvatarHairColor(opt.value as UGCAvatarConfig["hair_color"])}
                          className={cn(
                            "flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg border-2 transition-all flex-1",
                            avatarHairColor === opt.value
                              ? "border-indigo-500 bg-indigo-50"
                              : "border-border/60 hover:border-foreground/20"
                          )}
                        >
                          <div
                            className="w-6 h-6 rounded-full border border-black/10"
                            style={{ backgroundColor: opt.color }}
                          />
                          <span className="text-[8px] font-medium text-muted-foreground">{t(opt.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Vibe */}
                  <AvatarButtonGroup
                    label={t("video-avatar-vibe")}
                    options={AVATAR_VIBE_OPTIONS}
                    value={avatarVibe}
                    onChange={(v) => setAvatarVibe(v as UGCAvatarConfig["vibe"])}
                    t={t}
                  />

                  {/* Setting */}
                  <AvatarButtonGroup
                    label={t("video-avatar-setting")}
                    options={isPodcast ? PODCAST_SETTING_OPTIONS : AVATAR_SETTING_OPTIONS}
                    value={avatarSetting}
                    onChange={(v) => setAvatarSetting(v as UGCAvatarConfig["setting"])}
                    t={t}
                    columns={3}
                  />
                </div>
              </div>
            )}

            {/* Advanced Options */}
            <div className={cn(
              "rounded-xl overflow-hidden transition-all duration-200",
              showAdvanced
                ? "bg-white border-2 border-indigo-200 shadow-sm shadow-indigo-100/25"
                : "border border-border/60"
            )}>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium transition-colors",
                  showAdvanced
                    ? "text-indigo-700 bg-indigo-50/60"
                    : "text-muted-foreground hover:bg-muted/30"
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Sparkles className={cn("h-3 w-3", showAdvanced ? "text-indigo-500" : "text-muted-foreground")} />
                  <span className={cn(showAdvanced && "font-semibold")}>{t("video-advanced-options")}</span>
                </div>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-200",
                    showAdvanced && "rotate-180"
                  )}
                />
              </button>

              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3.5 space-y-3 border-t border-indigo-100 pt-3">
                      {/* User Prompt — optional, capped char limit */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] font-semibold text-indigo-600/80 uppercase tracking-wider">
                            {t("video-instruction")} <span className="normal-case text-gray-300">({t("video-optional")})</span>
                          </p>
                          <span
                            className={cn(
                              "text-[10px] tabular-nums",
                              userPrompt.length > INSTRUCTION_CHAR_LIMIT * 0.9
                                ? "text-amber-600 font-semibold"
                                : "text-gray-400"
                            )}
                          >
                            {userPrompt.length}/{INSTRUCTION_CHAR_LIMIT}
                          </span>
                        </div>
                        <textarea
                          value={userPrompt}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next.length <= INSTRUCTION_CHAR_LIMIT) {
                              setUserPrompt(next);
                            }
                          }}
                          maxLength={INSTRUCTION_CHAR_LIMIT}
                          placeholder={t(getInstructionPlaceholderKey(isUGC, isPodcast, videoTypeId))}
                          rows={3}
                          className="w-full rounded-lg border border-indigo-200/70 bg-indigo-50/30 px-3 py-2 text-xs placeholder:text-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:bg-white outline-none transition resize-none"
                        />
                      </div>

                      {/* Phase 5.4: el field "¿Qué problema resuelve?" se eliminó.
                          El director ya tiene product_description + sale_angle del Narrative,
                          que cubren ese contexto. Pedirlo de nuevo era redundante. */}

                      {/* Tone (hidden for sassy-object and animated-problem) */}
                      {!isCombo && (
                        <ToneAutocomplete
                          value={tone}
                          onChange={(v) => setTone(v)}
                          t={t}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {/* Fixed footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/60 shadow-[0_-4px_12px_rgba(0,0,0,0.03)] flex-shrink-0">
        {/* Phase 5.4: en flujo combo (sassy/animated) los créditos se cobran
            en approve, no acá. Mostrar el costo en este botón confunde al
            usuario porque al darle "Continuar" todavía no paga nada. */}
        {!isCombo && (
          <div className="flex items-center justify-center gap-1.5 mb-2.5 mx-auto w-fit px-3 py-1 rounded-lg bg-white border border-gray-200 shadow-sm">
            <span className="text-[11px] font-bold text-gray-800">
              {selectedStyle ? `1 ${t("video-count-video")}` : `0 ${t("video-count-videos")}`}
            </span>
            <span className="text-[11px] text-gray-400">·</span>
            <span className="text-[11px] font-medium text-gray-500">{t("video-cost")}</span>
            <IconCredit width="16px" height="12px" />
            <span className="text-[11px] font-bold text-[#4F46E5]">
              {totalCredits} {t("video-credits")}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={cn(
            "w-full flex items-center justify-center gap-2 h-10 text-sm font-bold rounded-xl",
            "text-white shadow-lg shadow-primary/20",
            "hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.005]",
            "active:scale-[0.995] transition-all duration-150",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          )}
          style={{ background: "linear-gradient(to right, #5050EA, #8484F0)" }}
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {isCombo ? t("video-studio-preparing") : t("video-generating")}
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              {/* Phase 5.4: en el flujo draft (sassy/animated) este botón
                  lleva al preview, no genera todavía. Decimos "Continuar"
                  para no implicar que se cobran los créditos acá. */}
              {isCombo ? t("video-studio-next") : t("video-generate")}
            </>
          )}
        </button>
        {selectedStyle && (
          <p className="text-[10px] text-gray-400 text-center mt-2">
            {t("video-generation-time")}
          </p>
        )}
      </div>
    </div>
  );
}

function getInstructionPlaceholderKey(isUGC: boolean, isPodcast: boolean, videoTypeId: string): string {
  if (isUGC) return "video-instruction-placeholder-ugc";
  if (isPodcast) return "video-instruction-placeholder-podcast";
  if (videoTypeId === "product-modeling") return "video-instruction-placeholder-modeling";
  if (videoTypeId === "animated-problem") return "video-instruction-placeholder-animated-problem";
  return "video-instruction-placeholder-sassy-object";
}

/** Tone autocomplete dropdown */
function ToneAutocomplete({
  value,
  onChange,
  t,
}: {
  value: string;
  onChange: (v: string) => void;
  t: (key: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const selectedLabel = value ? t(`tone-${value}`) : "";

  const filtered = TONE_OPTIONS.filter((opt) =>
    t(`tone-${opt.value}`).toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = () => {
    if (!open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (optValue: string) => {
    onChange(optValue);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={wrapperRef}>
      <p className="text-[10px] font-semibold text-indigo-600/80 mb-1 uppercase tracking-wider">
        {t("video-tone")}
      </p>
      <div
        onClick={handleOpen}
        className={cn(
          "w-full flex items-center gap-1 rounded-lg border border-indigo-200/70 bg-indigo-50/30 px-3 py-2 text-xs cursor-text transition",
          open && "border-indigo-500 ring-1 ring-indigo-500 bg-white"
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={open ? search : selectedLabel}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleOpen}
          placeholder={t("video-select-tone")}
          className="flex-1 bg-transparent outline-none text-xs placeholder:text-gray-400 text-foreground"
        />
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </div>

      <AnimatePresence>
        {open && (
          <>
            <div
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              aria-hidden
              onClick={() => { setOpen(false); setSearch(""); }}
            />
            <motion.div
              initial={{ opacity: 0, y: 4, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.96 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="fixed bg-white border border-indigo-200/70 rounded-xl shadow-xl overflow-hidden"
              style={{ zIndex: 9999, top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-2.5 text-xs text-gray-400 text-center">
                  {t("video-no-results")}
                </div>
              ) : (
                filtered.map((opt) => {
                  const isSelected = value === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleSelect(opt.value)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2.5 text-xs text-left transition-colors",
                        isSelected
                          ? "bg-indigo-50 text-indigo-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-indigo-500 flex-shrink-0" />}
                      <span className={cn(!isSelected && "ml-5")}>{t(`tone-${opt.value}`)}</span>
                    </button>
                  );
                })
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Reusable button group for avatar options */
function AvatarButtonGroup({
  label,
  options,
  value,
  onChange,
  t,
  columns = 2,
}: {
  label: string;
  options: readonly { value: string; labelKey: string }[];
  value: string;
  onChange: (v: string) => void;
  t: (key: string) => string;
  columns?: number;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-indigo-600/80 mb-1.5 uppercase tracking-wider">
        {label}
      </p>
      <div className={cn(
        "grid gap-1.5",
        columns === 2 && "grid-cols-2",
        columns === 3 && "grid-cols-3",
        columns === 4 && "grid-cols-4"
      )}>
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={cn(
              "px-2 py-1.5 rounded-lg border-2 text-[10px] font-medium transition-all text-center",
              value === opt.value
                ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                : "border-border/60 text-muted-foreground hover:border-foreground/20"
            )}
          >
            {t(opt.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
